import { forwardRef } from 'react'
import { cn } from '@/lib/cn'

export const Card = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function Card({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(
          'rounded-lg border border-border bg-card text-card-fg',
          className,
        )}
        {...props}
      />
    )
  },
)

export const CardHeader = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardHeader({ className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn('flex flex-col gap-1 border-b border-border px-4 py-3', className)}
        {...props}
      />
    )
  },
)

type Heading = 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'

interface CardTitleProps extends React.HTMLAttributes<HTMLHeadingElement> {
  /** Override the rendered heading level. Defaults to h3 to fit
   *  inside an existing page outline (CardTitle is usually a
   *  section heading, not a page heading). Pages where the card
   *  is the only landmark — e.g. /login — should pass `as="h1"`
   *  so the document outline + screen-reader rotor have a real
   *  page heading to anchor on. */
  as?: Heading
}

export const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  function CardTitle({ className, children, as: As = 'h3', ...props }, ref) {
    return (
      <As
        ref={ref}
        className={cn('text-base font-semibold leading-none', className)}
        {...props}
      >
        {children}
      </As>
    )
  },
)

export const CardContent = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  function CardContent({ className, ...props }, ref) {
    return <div ref={ref} className={cn('p-4', className)} {...props} />
  },
)
